import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { FormControl, Grid, InputLabel } from "@material-ui/core";
import {
  Paper,
  Heading,
  PrimaryButton,
  Dropdown,
  Input,
  TaskNameInput,
  WorkflowNameInput,
  Button,
} from "../../components";

import { TASK_STATUSES, SEARCH_TASK_TYPES_SET } from "../../utils/constants";
import { useQueryState } from "react-router-use-location-state";
import SearchTabs from "./SearchTabs";
import TaskResultsTable from "./TaskResultsTable";
import DateRangePicker from "../../components/DateRangePicker";
import { DEFAULT_ROWS_PER_PAGE } from "../../components/DataTable";
import { useTaskSearch } from "../../data/task";

import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import executionsStyles from "./executionsStyles";
import sharedStyles, { dataTableStyles } from "../styles";
import { FilterList } from "@material-ui/icons";

import {
  NavLink,
  DataTable,
  LinearProgress,
  TertiaryButton,
  Text,
} from "../../components";
import { Alert, AlertTitle } from "@material-ui/lab";
import BulkActionModule from "./BulkActionModule";
import TaskLink from "../../components/TaskLink";

const useStyles = makeStyles({
  ...executionsStyles,
  ...sharedStyles,
});

// /////
const executionFields = [
  { name: "updateTime", label: "Update Time", type: "date" },
  { name: "scheduledTime", label: "Scheduled Time", type: "date" },
  { name: "startTime", label: "Start Time", type: "date" },
  { name: "endTime", label: "End Time", type: "date" },
  {
    name: "taskId",
    label: "Task ID",
    grow: 1.5,
    renderer: (taskId, row) => (
      <TaskLink taskId={taskId} workflowId={row.workflowId} />
    ),
  },
  {
    name: "taskDefName",
    label: "Task Name",
    grow: 1.5,
    renderer: (taskDefName) =>
      SEARCH_TASK_TYPES_SET.has(taskDefName) ? "-" : taskDefName,
  },
  {
    name: "taskType",
    label: "Task Type",
    grow: 0.6,

    renderer: (taskType) =>
      SEARCH_TASK_TYPES_SET.has(taskType) ? taskType : "SIMPLE",
  },
  {
    name: "workflowId",
    label: "Workflow ID",
    grow: 2,
    renderer: (workflowId) => (
      <NavLink path={`/execution/${workflowId}`}>{workflowId}</NavLink>
    ),
  },
  { name: "workflowType", label: "Workflow Name", grow: 1.5 },
  {
    name: "executionTime",
    label: "Execution Time",
    grow: 0.6,
  },
  {
    name: "queueWaitTime",
    label: "Queue Wait Time",
    grow: 0.6,
  },
  {
    name: "workflowPriority",
    label: "Workflow Priority",
    grow: 0.6,
  },
  {
    name: "status",
    label: "Status",
  },
  { name: "input", label: "Input", grow: 3, wrap: true },
  { name: "output", label: "Output", grow: 3, wrap: true },
  {
    name: "reasonForIncompletion",
    label: "Reason for Incompletion",
    grow: 3,

    wrap: true,
  },
];

function ShowMore({
  rowsPerPage,
  rowCount,
  onChangePage,
  onChangeRowsPerPage,
  currentPage,
}) {
  return (
    <div style={{ textAlign: "center", padding: 15 }}>
      <TertiaryButton onClick={() => onChangePage(currentPage + 1)}>
        Show More Results
      </TertiaryButton>
    </div>
  );
}
// /////

const DEFAULT_SORT = "startTime:DESC";
const MS_IN_DAY = 86400000;
const taskTypeOptions = Array.from(SEARCH_TASK_TYPES_SET.values());

export default function TaskSearch(props) {
  const permissions = props?.permissions || null;
  const classes = useStyles();

  const [freeText, setFreeText] = useQueryState("freeText", "");
  const [status, setStatus] = useQueryState("status", []);
  const [taskName, setTaskName] = useQueryState("taskName", []);
  const [taskId, setTaskId] = useQueryState("taskId", "");
  const [taskType, setTaskType] = useQueryState("taskType", []);
  const [startFrom, setStartFrom] = useQueryState("startFrom", "");
  const [startTo, setStartTo] = useQueryState("startTo", "");
  const [lookback, setLookback] = useQueryState("lookback", "");
  const [workflowType, setWorkflowType] = useQueryState("workflowType", []);

  const [page, setPage] = useQueryState("page", 1);
  const [rowsPerPage, setRowsPerPage] = useQueryState(
    "rowsPerPage",
    DEFAULT_ROWS_PER_PAGE
  );
  const [sort, setSort] = useQueryState("sort", DEFAULT_SORT);
  const [queryFT, setQueryFT] = useState(buildQuery);
  const [filterClicked, setFilterClicked] = useState(false);
  const [showSubHeader, setShowSubHeader] = useState(false);

  const {
    data: resultObj,
    error,
    isFetching,
    refetch,
  } = useTaskSearch({
    page,
    rowsPerPage,
    sort,
    query: queryFT.query,
    freeText: queryFT.freeText,
  });

  function buildQuery() {
    const clauses = [];
    if (!_.isEmpty(taskName)) {
      clauses.push(`taskDefName IN (${taskName.join(",")})`);
    }
    if (!_.isEmpty(taskType)) {
      clauses.push(`taskType IN (${taskType.join(",")})`);
    }
    if (!_.isEmpty(taskId)) {
      clauses.push(`taskId="${taskId}"`);
    }
    if (!_.isEmpty(status)) {
      clauses.push(`status IN (${status.join(",")})`);
    }
    if (!_.isEmpty(lookback)) {
      clauses.push(`updateTime>${new Date().getTime() - lookback * MS_IN_DAY}`);
    }
    if (!_.isEmpty(startFrom)) {
      clauses.push(`updateTime>${new Date(startFrom).getTime()}`);
    }
    if (!_.isEmpty(startTo)) {
      clauses.push(`updateTime<${new Date(startTo).getTime()}`);
    }
    if (!_.isEmpty(workflowType)) {
      clauses.push(`workflowType IN (${workflowType.join(",")})`);
    }
    return {
      query: clauses.join(" AND "),
      freeText: _.isEmpty(freeText) ? "*" : freeText,
    };
  }

  function doSearch() {
    setPage(1);
    const oldQueryFT = queryFT;
    const newQueryFT = buildQuery();
    setQueryFT(newQueryFT);

    // Only force refetch if query didn't change. Else let react-query detect difference and refetch automatically
    if (_.isEqual(oldQueryFT, newQueryFT)) {
      refetch();
    }
  }

  const handleExecutionFilterClick = (e) => {
    e.stopPropagation();
    setShowSubHeader(!showSubHeader);
  };

  const handlePage = (page) => {
    setPage(page);
  };

  const handleSort = (changedColumn, direction) => {
    const sort = `${changedColumn}:${direction.toUpperCase()}`;
    setPage(1);
    setSort(sort);
  };

  const handleRowsPerPage = (rowsPerPage) => {
    setPage(1);
    setRowsPerPage(rowsPerPage);
  };

  const handleLookback = (val) => {
    setStartFrom("");
    setStartTo("");
    setLookback(val);
  };

  const handleStartFrom = (val) => {
    setLookback("");
    setStartFrom(val);
  };

  const handleStartTo = (val) => {
    setLookback("");
    setStartTo(val);
  };
  const handleFilterClick = (event) => {
    event.stopPropagation();
    setFilterClicked(!filterClicked);
  };

  let totalHits = 0;
  if (resultObj) {
    if (resultObj.totalHits) {
      totalHits = resultObj.totalHits;
    } else {
      if (resultObj.results) {
        totalHits = resultObj.results.length;
      }
    }
  }
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const tableRef = useRef(null);

  const defaultSortField = sort ? sort.split(":")[0] : null;
  const defaultSortDirection = sort ? sort.split(":")[1] : null;

  useEffect(() => {
    setSelectedRows([]);
    setToggleCleared((t) => !t);
  }, [resultObj]);

  return (
    <div className={clsx([classes.wrapper])}>
      {/* <Heading level={3} className={classes.heading}>
        Search Executions
      </Heading> */}
      <Paper className={classes.paper}>
        {<SearchTabs permissions={permissions} />}
        {/* <Button className={classes.filterBtn}>
          <div
            className={classes.filterIconContainer}
            onClick={handleFilterClick}
          >
            <span className="">Filters</span>
            <FilterList color="primary" />
          </div>
        </Button> */}
        {/* <Grid container spacing={3} className={`${classes.controls} `}>
          <Grid item xs={3}>
            <TaskNameInput
              fullWidth
              onChange={(evt, val) => setTaskName(val)}
              value={taskName}
            />
          </Grid>
          <Grid item xs={3}>
            <Input
              fullWidth
              label="Task ID"
              defaultValue={taskId}
              onBlur={setTaskId}
              clearable
            />
          </Grid>
          <Grid item xs={3}>
            <Dropdown
              label="Task Status"
              fullWidth
              options={TASK_STATUSES}
              multiple
              onChange={(evt, val) => setStatus(val)}
              value={status}
            />
          </Grid>
          <Grid item xs={3}>
            <Dropdown
              label="Task Type (Leave blank to include SIMPLE Tasks)"
              fullWidth
              options={taskTypeOptions}
              multiple
              onChange={(evt, val) => setTaskType(val)}
              value={taskType}
            />
          </Grid>
          <Grid item xs={3}>
            <WorkflowNameInput
              fullWidth
              onChange={(evt, val) => setWorkflowType(val)}
              value={workflowType}
            />
          </Grid>
          <Grid item xs={4}>
            <DateRangePicker
              disabled={!_.isEmpty(lookback)}
              label="Update Time"
              from={startFrom}
              to={startTo}
              onFromChange={handleStartFrom}
              onToChange={handleStartTo}
            />
          </Grid>
          <Grid item xs={1}>
            <Input
              fullWidth
              label="Lookback (days)"
              defaultValue={lookback}
              onBlur={handleLookback}
              type="number"
              clearable
              disabled={!_.isEmpty(startFrom) || !_.isEmpty(startTo)}
            />
          </Grid>

          <Grid item xs={3}>
            <Input
              fullWidth
              label="Lucene-syntax Query (Double-quote strings for Free Text)"
              defaultValue={freeText}
              onBlur={setFreeText}
              clearable
            />
          </Grid>
          <Grid item xs={1}>
            <FormControl>
              <InputLabel>&nbsp;</InputLabel>
              <PrimaryButton onClick={doSearch}>Search</PrimaryButton>
            </FormControl>
          </Grid>
        </Grid> */}
      </Paper>
      {/* <TaskResultsTable
        resultObj={resultObj}
        error={error}
        busy={isFetching}
        page={page}
        rowsPerPage={rowsPerPage}
        sort={sort}
        setPage={handlePage}
        setRowsPerPage={handleRowsPerPage}
        setSort={handleSort}
      /> */}
      <Paper className={classes.paper}>
        {isFetching && <LinearProgress />}
        {error && (
          <Alert severity="error">
            <AlertTitle>Query Failed</AlertTitle>
            {error.message}
          </Alert>
        )}
        {!resultObj && !error && (
          <Text className={classes.clickSearch}>
            Click "Search" to submit query.
          </Text>
        )}
        {resultObj && (
          <DataTable
            title={totalHits > 0 && ` Page ${page} of ${totalHits}`}
            showRefresh={true}
            handleRefreshClick={() => {
              refetch();
            }}
            data={resultObj.results}
            columns={executionFields}
            defaultShowColumns={[
              "updateTime",
              "taskId",
              "taskDefName",
              "workflowType",
              "executionType",
              "taskType",
              "status",
            ]}
            localStorageKey="taskResultsTable"
            keyField="taskId"
            executionFilter
            handleExecutionFilterClick={handleExecutionFilterClick}
            paginationServer
            paginationTotalRows={totalHits}
            paginationDefaultPage={page}
            paginationPerPage={rowsPerPage}
            onChangeRowsPerPage={(rowsPerPage) => setRowsPerPage(rowsPerPage)}
            onChangePage={(page) => setPage(page)}
            sortServer
            defaultSortField={defaultSortField}
            defaultSortAsc={defaultSortDirection === "ASC"}
            onSort={(column, sortDirection) => {
              setSort(column.id, sortDirection);
            }}
            selectableRows
            subHeader={showSubHeader}
            subHeaderComponent={
              <Grid container spacing={3} className={`${classes.controls} `}>
                <Grid item xs={3}>
                  <TaskNameInput
                    fullWidth
                    onChange={(evt, val) => setTaskName(val)}
                    value={taskName}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Input
                    fullWidth
                    label="Task ID"
                    defaultValue={taskId}
                    onBlur={setTaskId}
                    clearable
                  />
                </Grid>
                <Grid item xs={3}>
                  <Dropdown
                    label="Task Status"
                    fullWidth
                    options={TASK_STATUSES}
                    multiple
                    onChange={(evt, val) => setStatus(val)}
                    value={status}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Dropdown
                    label="Task Type (Leave blank to include SIMPLE Tasks)"
                    fullWidth
                    options={taskTypeOptions}
                    multiple
                    onChange={(evt, val) => setTaskType(val)}
                    value={taskType}
                  />
                </Grid>
                <Grid item xs={3}>
                  <WorkflowNameInput
                    fullWidth
                    onChange={(evt, val) => setWorkflowType(val)}
                    value={workflowType}
                  />
                </Grid>
                <Grid item xs={4}>
                  <DateRangePicker
                    disabled={!_.isEmpty(lookback)}
                    label="Update Time"
                    from={startFrom}
                    to={startTo}
                    onFromChange={handleStartFrom}
                    onToChange={handleStartTo}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Input
                    fullWidth
                    label="Lookback (days)"
                    defaultValue={lookback}
                    onBlur={handleLookback}
                    type="number"
                    clearable
                    disabled={!_.isEmpty(startFrom) || !_.isEmpty(startTo)}
                  />
                </Grid>

                <Grid item xs={3}>
                  <Input
                    fullWidth
                    label="Lucene-syntax Query (Double-quote strings for Free Text)"
                    defaultValue={freeText}
                    onBlur={setFreeText}
                    clearable
                  />
                </Grid>
                <Grid item xs={1}>
                  <FormControl>
                    <InputLabel>&nbsp;</InputLabel>
                    <PrimaryButton onClick={doSearch}>Search</PrimaryButton>
                  </FormControl>
                </Grid>
              </Grid>
            }
            contextComponent={
              <BulkActionModule
                selectedRows={selectedRows}
                popperAnchorEl={tableRef.current}
              />
            }
            onSelectedRowsChange={({ selectedRows }) =>
              setSelectedRows(selectedRows)
            }
            clearSelectedRows={toggleCleared}
            customStyles={{
              ...dataTableStyles,
              header: {
                style: {
                  // overflow: "visible",
                  ...dataTableStyles.header,
                },
              },
              contextMenu: {
                style: {
                  display: "none",
                },
                activeStyle: {
                  display: "flex",
                },
              },
            }}
            // paginationComponent={showMore ? ShowMore : null}
            fixedHeader
            fixedHeaderScrollHeight="350px"
          />
        )}
      </Paper>
    </div>
  );
}
